// Procedural surface, camo and decal-atlas painters. They draw into a 2D context (OffscreenCanvas in
// the texture worker, a canvas on the main thread as a fallback) and know nothing else about the DOM.
// Every painter is a generator that yields between steps: the worker simply runs it to the end, the
// main-thread fallback yields to the event loop between steps so the page stays responsive.
//
// Every surface pattern is seamless: noise comes from periodic TileNoise, stripe periods divide the
// texture size, and shapes that cross a tile edge are drawn again shifted by +-size. The tile is
// repeated once per `uvScale` metres on walls and floors alike, so wear stays low-frequency and
// symmetric about the tile edge (no obvious "ground" side) and fades out in the mip chain.

import { DECAL_GRID, DECAL_KINDS } from "./decalKinds";
import type { DecalKind } from "./decalKinds";
import { hashString, mulberry32, TileNoise } from "./noise";
import { ARCH, luminance, mix, parseHex, rgba, shade } from "./palette";
import type { Rgb } from "./palette";
import type { TextureJob } from "./textureSpec";
import type { CamoName, TextureKind } from "./types";

/** The one 2D API the painters use; the main-thread canvas fallback is cast to it. */
export type Ctx2D = OffscreenCanvasRenderingContext2D;

export type Steps = Generator<void, void, undefined>;

const TAU = Math.PI * 2;
const BLACK = "#000000";
const WHITE = "#ffffff";
/** Warm dirt tone used for grime stains. */
const DIRT = "#2b261f";
const STRIPE_DARK = "#18191c";
/** Bare steel under chipped paint. */
const BARE_STEEL = "#b4b7bb";
/** Rows of pixels shaded between two yields. */
const ROWS_PER_STEP = 32;

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** Everything a painter needs. `grime` is already quantised to 0, .5 or 1. */
interface GenCtx {
  readonly g: Ctx2D;
  readonly size: number;
  readonly color: string;
  readonly base: Rgb;
  readonly grime: number;
  readonly seed: number;
  /** Seeded PRNG; draw order is fixed, so output is deterministic. */
  readonly rand: () => number;
}

/** Mutable pixel the per-pixel shaders write 0..255 channels into. */
interface Px {
  r: number;
  g: number;
  b: number;
}

function clamp01(v: number): number {
  return v > 0 ? (v < 1 ? v : 1) : 0;
}

/** Hermite smoothstep; also works reversed (e0 > e1). */
function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}

/** Moves `px` towards `to` by t. */
function blend(px: Px, to: Rgb, t: number): void {
  px.r += (to.r - px.r) * t;
  px.g += (to.g - px.g) * t;
  px.b += (to.b - px.b) * t;
}

/** k-quantile (0..1) of a sample set; used to pick thresholds that give a fixed area coverage. */
function quantile(values: Float32Array, q: number): number {
  const sorted = Float32Array.from(values).sort();
  return sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
}

/**
 * Runs `shader` for every pixel and writes the result as opaque RGB. Replaces the canvas content,
 * so call it before any Canvas2D overlay. u, v are pixel-centre coordinates in tile units (0..1),
 * v counting from the top row of the picture.
 */
function* paintPixels(c: GenCtx, shader: (x: number, y: number, u: number, v: number, px: Px) => void): Steps {
  const { g, size } = c;
  const img = g.createImageData(size, size);
  const data = img.data;
  const px: Px = { r: 0, g: 0, b: 0 };
  const inv = 1 / size;
  let i = 0;
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) * inv;
    for (let x = 0; x < size; x++) {
      shader(x, y, (x + 0.5) * inv, v, px);
      // Uint8ClampedArray rounds and clamps.
      data[i++] = px.r;
      data[i++] = px.g;
      data[i++] = px.b;
      data[i++] = 255;
    }
    if (y % ROWS_PER_STEP === ROWS_PER_STEP - 1) yield;
  }
  g.putImageData(img, 0, 0);
}

/**
 * Calls `draw` once for the shape itself and again for every wrap-around copy (shifted by +-size)
 * that is needed because the bounding box [x0,x1] x [y0,y1] leaves the tile. `draw` must use
 * absolute coordinates and must not consume random numbers (all copies have to be identical).
 */
function drawWrapped(
  g: Ctx2D,
  size: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  draw: () => void,
): void {
  for (let ix = 0; ix < 3; ix++) {
    if (ix === 1 && x1 <= size) continue;
    if (ix === 2 && x0 >= 0) continue;
    const dx = ix === 0 ? 0 : ix === 1 ? -size : size;
    for (let iy = 0; iy < 3; iy++) {
      if (iy === 1 && y1 <= size) continue;
      if (iy === 2 && y0 >= 0) continue;
      const dy = iy === 0 ? 0 : iy === 1 ? -size : size;
      if (dx === 0 && dy === 0) {
        draw();
      } else {
        g.save();
        g.translate(dx, dy);
        draw();
        g.restore();
      }
    }
  }
}

/** fillRect for a rectangle starting inside the tile; wraps the part that sticks out. */
function fillRectWrapped(g: Ctx2D, size: number, x: number, y: number, w: number, h: number): void {
  g.fillRect(x, y, w, h);
  const wx = x + w > size;
  const wy = y + h > size;
  if (wx) g.fillRect(x - size, y, w, h);
  if (wy) g.fillRect(x, y - size, w, h);
  if (wx && wy) g.fillRect(x - size, y - size, w, h);
}

function fillCircle(g: Ctx2D, x: number, y: number, r: number): void {
  g.beginPath();
  g.arc(x, y, r, 0, TAU);
  g.fill();
}

/** Randomly placed 1..few px dots in one fill style. */
function speckle(c: GenCtx, style: string, count: number, minSize: number, maxSize: number): void {
  const { g, size, rand } = c;
  g.fillStyle = style;
  for (let i = 0; i < count; i++) {
    const w = minSize + Math.floor(rand() * (maxSize - minSize + 1));
    const h = rand() < 0.5 ? w : minSize + Math.floor(rand() * (maxSize - minSize + 1));
    fillRectWrapped(g, size, Math.floor(rand() * size), Math.floor(rand() * size), w, h);
  }
}

/** Soft elliptical smudge: `color` at `alpha` in the centre fading to nothing at the rim. */
function softBlob(
  g: Ctx2D,
  size: number,
  x: number,
  y: number,
  rx: number,
  ry: number,
  color: string,
  alpha: number,
): void {
  drawWrapped(g, size, x - rx, y - ry, x + rx, y + ry, () => {
    g.save();
    g.translate(x, y);
    g.scale(rx, ry);
    const grad = g.createRadialGradient(0, 0, 0, 0, 0, 1);
    grad.addColorStop(0, rgba(color, alpha));
    grad.addColorStop(1, rgba(color, 0));
    g.fillStyle = grad;
    fillCircle(g, 0, 0, 1);
    g.restore();
  });
}

interface ScratchOpts {
  count: number;
  minLen: number;
  maxLen: number;
  /** Mean direction in radians. */
  angle: number;
  /** Total angular spread in radians. */
  spread: number;
  /** Stroke styles; each scratch picks one at random. */
  light: string;
  dark: string;
  width: number;
}

/** Thin wrapped scratch lines in a light or dark style. */
function drawScratches(c: GenCtx, o: ScratchOpts): void {
  const { g, size, rand } = c;
  g.lineCap = "round";
  for (let i = 0; i < o.count; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const len = o.minLen + rand() * (o.maxLen - o.minLen);
    const a = o.angle + (rand() - 0.5) * o.spread;
    const dx = Math.cos(a) * len;
    const dy = Math.sin(a) * len;
    const style = rand() < 0.5 ? o.light : o.dark;
    const width = o.width * (0.6 + rand() * 0.8);
    drawWrapped(
      g,
      size,
      Math.min(x, x + dx) - 1,
      Math.min(y, y + dy) - 1,
      Math.max(x, x + dx) + 1,
      Math.max(y, y + dy) + 1,
      () => {
        g.strokeStyle = style;
        g.lineWidth = width;
        g.beginPath();
        g.moveTo(x, y);
        g.lineTo(x + dx, y + dy);
        g.stroke();
      },
    );
  }
}

/** Domed rivet / screw head lit from the top-left, with a cast shadow towards the bottom-right. */
function drawRivet(g: Ctx2D, x: number, y: number, r: number, color: string): void {
  g.fillStyle = rgba(BLACK, 0.35);
  fillCircle(g, x + 1.2, y + 1.6, r + 0.6);
  const dome = g.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r);
  dome.addColorStop(0, shade(color, 1.35));
  dome.addColorStop(1, shade(color, 0.75));
  g.fillStyle = dome;
  fillCircle(g, x, y, r);
  g.strokeStyle = rgba(BLACK, 0.3);
  g.lineWidth = 0.8;
  g.beginPath();
  g.arc(x, y, r, 0, TAU);
  g.stroke();
  g.fillStyle = rgba(WHITE, 0.55);
  fillCircle(g, x - r * 0.35, y - r * 0.4, r * 0.25);
}

/** Standard HSL (all 0..1) to 0..255 channels. */
function hslToPx(h: number, s: number, l: number, out: Px): void {
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t0: number): number => {
    const t = t0 - Math.floor(t0);
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  out.r = channel(h + 1 / 3) * 255;
  out.g = channel(h) * 255;
  out.b = channel(h - 1 / 3) * 255;
}

/**
 * Diagonal (45 degree) two-colour stripes. The pattern is a function of (x + y) mod period, so it
 * tiles whenever `period` divides the texture size. `jitter` wobbles the edges for a worn look.
 */
function* paintStripes(
  c: GenCtx,
  period: number,
  colA: Rgb,
  colB: Rgb,
  jitter?: { noise: TileNoise; amp: number },
): Steps {
  const wt = 1.5 * Math.SQRT2; // ~1.5 px edge softness measured perpendicular to the stripes
  const quarter = period * 0.25;
  yield* paintPixels(c, (x, y, u, v, px) => {
    // Signed offset from the centre of the B stripe, wrapped into [-period/2, period/2).
    let s = ((x + y + 1) % period) - period * 0.75;
    if (s < -period * 0.5) s += period;
    if (jitter) s += (jitter.noise.sample(u, v) - 0.5) * jitter.amp;
    const b = clamp01(0.5 + (quarter - Math.abs(s)) / wt);
    px.r = colA.r + (colB.r - colA.r) * b;
    px.g = colA.g + (colB.g - colA.g) * b;
    px.b = colA.b + (colB.b - colA.b) * b;
  });
}

/** Distance in px from the pixel centre to the nearest tile border. */
function borderDistance(x: number, y: number, size: number): number {
  return Math.min(x, y, size - 1 - x, size - 1 - y) + 0.5;
}

/**
 * Dirt that gathers towards the bottom of the picture and, to keep the tile seamless, continues over
 * the wrap just above its top edge. v counts from the top, 0..1. Subtle by design.
 */
function baseDirt(v: number): number {
  return Math.max(smoothstep(0.72, 1, v), 1 - smoothstep(0, 0.05, v));
}

// ---------------------------------------------------------------------------
// Surface painters
// ---------------------------------------------------------------------------

function* drawPlain(c: GenCtx): Steps {
  const { base, grime, seed, rand } = c;
  const tone = new TileNoise(seed ^ 0x0a01, 3, 3);
  const blotch = new TileNoise(seed ^ 0x0a02, 4, 4);
  yield* paintPixels(c, (_x, _y, u, v, px) => {
    // Clean paint: a hint of low-frequency tone, fine grain and, with grime, soft stains.
    let f = 0.985 + (tone.fbm(u, v, 2) - 0.5) * 0.03 + (rand() - 0.5) * 0.02;
    if (grime > 0) f *= 1 - grime * 0.12 * smoothstep(0.5, 0.78, blotch.fbm(u, v, 2)) - grime * 0.03 * baseDirt(v);
    px.r = base.r * f;
    px.g = base.g * f;
    px.b = base.b * f;
  });
  speckle(c, rgba(BLACK, 0.06), Math.round(c.size * 0.4), 1, 2);
}

function* drawConcrete(c: GenCtx): Steps {
  const { g, size, color, grime, base, seed, rand } = c;
  const tone = new TileNoise(seed ^ 0x1001, 4, 4);
  const fine = new TileNoise(seed ^ 0x1002, 48, 48);
  const drip = new TileNoise(seed ^ 0x1003, 26, 2); // long in v: vertical streaks
  const dirt = new TileNoise(seed ^ 0x1004, 3, 3);
  const chips = new TileNoise(seed ^ 0x1005, 40, 40);
  yield* paintPixels(c, (x, y, u, v, px) => {
    // Blotchy tone variation (about +-6 %) with fine surface mottling.
    let f = 0.94 + (tone.fbm(u, v, 3) - 0.5) * 0.12 + (fine.sample(u, v) - 0.5) * 0.05;
    // Subtle vertical drip stains, stronger with grime.
    f -= smoothstep(0.62, 0.86, drip.sample(u, v)) * (0.03 + 0.08 * grime);
    // Form-panel seam: darker line on the tile border (one px per side, so 2 px when tiled), with
    // chipped, irregular spalling along the arris.
    const de = borderDistance(x, y, size);
    let dark = 0.16 * Math.exp(-de * de * 0.5) * (1 + grime * 1.2);
    dark += 0.1 * smoothstep(0.58, 0.78, chips.sample(u, v)) * Math.exp(-de / 9);
    dark += (0.03 + 0.05 * grime) * baseDirt(v);
    if (grime > 0) {
      // Stains plus dirt gathering near the seams.
      const s = dirt.fbm(u, v, 3);
      dark += grime * (0.2 * smoothstep(0.5, 0.78, s) + 0.12 * Math.exp(-de / 20) * (0.4 + s));
    }
    f *= 1 - dark;
    px.r = base.r * f;
    px.g = base.g * f;
    px.b = base.b * f;
  });
  yield;

  // Small dark pits with a faint lit lower rim.
  const pitDark = shade(color, 0.45);
  for (let i = 0; i < 110; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = (0.7 + rand() * 1.3) * (rand() < 0.1 ? 1.8 : 1);
    const dark = rgba(pitDark, 0.3 + rand() * 0.25);
    drawWrapped(g, size, x - r - 1, y - r - 1, x + r + 2, y + r + 2, () => {
      g.fillStyle = rgba(WHITE, 0.14);
      fillCircle(g, x + 0.7, y + 0.7, r + 0.4);
      g.fillStyle = dark;
      fillCircle(g, x, y, r);
    });
  }

  // Four form-tie holes, each with a drip streak underneath.
  const inset = size * 0.125;
  const streakLen = size * 0.11;
  for (const hy of [inset, size - inset]) {
    for (const hx of [inset, size - inset]) {
      const streak = g.createLinearGradient(0, hy, 0, hy + streakLen);
      streak.addColorStop(0, rgba(BLACK, 0.05 + 0.1 * grime));
      streak.addColorStop(1, rgba(BLACK, 0));
      g.fillStyle = streak;
      g.fillRect(hx - 4, hy, 8, streakLen);
      g.fillStyle = rgba(BLACK, 0.1);
      fillCircle(g, hx, hy, 9);
      g.fillStyle = rgba(WHITE, 0.22);
      fillCircle(g, hx + 0.8, hy + 1, 6.6);
      const hole = g.createRadialGradient(hx - 1.5, hy - 1.5, 0.5, hx, hy, 6);
      hole.addColorStop(0, rgba("#0b0b0c", 0.85));
      hole.addColorStop(1, rgba("#1c1c1e", 0.7));
      g.fillStyle = hole;
      fillCircle(g, hx, hy, 5.6);
    }
  }
}

function* drawTiles(c: GenCtx): Steps {
  const { g, size, color, grime, rand } = c;
  const n = 4;
  const cell = size / n;
  const gap = 2; // half a joint: neighbouring tiles leave a 4 px grout line
  const face = cell - 2 * gap;
  const grout = mix(mix(shade(color, 0.66), ARCH.darkTrim, 0.15), "#121314", grime * 0.45);
  g.fillStyle = grout;
  g.fillRect(0, 0, size, size);

  for (let ty = 0; ty < n; ty++) {
    for (let tx = 0; tx < n; tx++) {
      const x0 = tx * cell + gap;
      const y0 = ty * cell + gap;
      g.fillStyle = shade(color, 0.94 + (rand() - 0.5) * 0.06);
      g.fillRect(x0, y0, face, face);
      // Glaze: a touch lighter towards the top-left, darker towards the bottom-right.
      const glaze = g.createLinearGradient(x0, y0, x0 + face, y0 + face);
      glaze.addColorStop(0, rgba(WHITE, 0.08));
      glaze.addColorStop(1, rgba(BLACK, 0.06));
      g.fillStyle = glaze;
      g.fillRect(x0, y0, face, face);
      // Bevel: light on the top/left edges, faint shade on the bottom/right edges.
      g.fillStyle = rgba(WHITE, 0.6);
      g.fillRect(x0, y0, face, 2);
      g.fillRect(x0, y0 + 2, 2, face - 2);
      g.fillStyle = rgba(BLACK, 0.16);
      g.fillRect(x0 + 2, y0 + face - 2, face - 2, 2);
      g.fillRect(x0 + face - 2, y0 + 2, 2, face - 4);
      // Chipped corner: a small irregular notch in the grout colour.
      if (rand() < 0.45) {
        const right = rand() < 0.5;
        const bottom = rand() < 0.5;
        const cx = right ? x0 + face : x0;
        const cy = bottom ? y0 + face : y0;
        const sx = right ? -1 : 1;
        const sy = bottom ? -1 : 1;
        const a = 4 + rand() * 9;
        const b = 4 + rand() * 9;
        g.fillStyle = rgba(grout, 0.8);
        g.beginPath();
        g.moveTo(cx, cy);
        g.lineTo(cx + sx * a, cy);
        g.lineTo(cx + sx * a * 0.55, cy + sy * b * 0.4);
        g.lineTo(cx + sx * a * 0.2, cy + sy * b);
        g.lineTo(cx, cy + sy * b);
        g.closePath();
        g.fill();
      }
    }
  }
  speckle(c, rgba(BLACK, 0.05), 220, 1, 1);
  yield;

  if (grime > 0) {
    // Dirt collects where joints cross, plus blotches on the faces.
    for (let j = 0; j <= n; j++) {
      for (let i = 0; i <= n; i++) softBlob(g, size, i * cell, j * cell, 26, 26, DIRT, 0.22 * grime);
    }
    const blobs = Math.round(grime * 18);
    for (let i = 0; i < blobs; i++) {
      softBlob(
        g,
        size,
        rand() * size,
        rand() * size,
        24 + rand() * 24,
        24 + rand() * 24,
        DIRT,
        0.04 + 0.1 * grime * rand(),
      );
    }
  }
}

function* drawPanel(c: GenCtx): Steps {
  const { g, size, color, base, grime, seed, rand } = c;
  const half = size / 2;
  const brush = new TileNoise(seed ^ 0x2001, 2, 160);
  const dirt = new TileNoise(seed ^ 0x2002, 4, 4);
  const chips = new TileNoise(seed ^ 0x2003, 36, 36);
  const bare = parseHex(mix(color, WHITE, 0.45));
  const panelTone = [0, 1, 2, 3].map(() => 1 + (rand() - 0.5) * 0.05);
  yield* paintPixels(c, (x, y, u, v, px) => {
    const f0 = panelTone[(y >= half ? 2 : 0) + (x >= half ? 1 : 0)];
    // Faint horizontal brushing.
    let f = f0 * (1 + (brush.fbm(u, v, 2) - 0.5) * 0.08);
    // Distance to the nearest seam: ambient occlusion plus grime gathering along the seams.
    const mx = x % half;
    const my = y % half;
    const de = Math.min(mx, half - 1 - mx, my, half - 1 - my) + 0.5;
    let dark = 0.08 * Math.exp(-de / 10);
    // Each panel is dirtier at its own bottom edge (rain runs down and pools on the lip).
    dark += 0.05 * smoothstep(0.7, 1, (my + 0.5) / half) * (0.5 + grime);
    if (grime > 0) {
      const s = dirt.fbm(u, v, 2);
      dark += grime * (0.3 * Math.exp(-de / 14) * (0.35 + 0.65 * s) + 0.1 * smoothstep(0.55, 0.8, s));
    }
    f *= 1 - dark;
    px.r = base.r * f;
    px.g = base.g * f;
    px.b = base.b * f;
    // Chipped paint along the seams shows brighter bare metal.
    const wear = smoothstep(0.56, 0.76, chips.sample(u, v)) * Math.exp(-de / 5);
    if (wear > 0.01) blend(px, bare, 0.65 * wear);
  });
  yield;

  // Recessed seams (dark line + light bevel on the lower/right edge). Seams at 0 and size are
  // the same seam seen from both tile edges.
  for (const p of [0, half, size]) {
    g.fillStyle = rgba("#0a0a0c", 0.6);
    g.fillRect(p - 1, 0, 2, size);
    g.fillRect(0, p - 1, size, 2);
    g.fillStyle = rgba(WHITE, 0.3);
    g.fillRect(p + 1, 0, 1, size);
    g.fillRect(0, p + 1, size, 1);
  }

  // Rivets at the corners and edge midpoints of every panel, inset clear of the seams.
  const inset = 14;
  const rivetColor = shade(color, 1.05);
  for (let py = 0; py < 2; py++) {
    for (let px = 0; px < 2; px++) {
      const x0 = px * half + inset;
      const x1 = (px + 1) * half - inset;
      const xm = px * half + half / 2;
      const y0 = py * half + inset;
      const y1 = (py + 1) * half - inset;
      const ym = py * half + half / 2;
      const spots: Array<[number, number]> = [
        [x0, y0],
        [xm, y0],
        [x1, y0],
        [x0, ym],
        [x1, ym],
        [x0, y1],
        [xm, y1],
        [x1, y1],
      ];
      for (const [rx, ry] of spots) {
        if (grime > 0) {
          const streak = g.createLinearGradient(0, ry, 0, ry + 34);
          streak.addColorStop(0, rgba(DIRT, 0.14 * grime));
          streak.addColorStop(1, rgba(DIRT, 0));
          g.fillStyle = streak;
          g.fillRect(rx - 1.5, ry, 3, 34);
        }
        drawRivet(g, rx, ry, 3.6, rivetColor);
      }
    }
  }
}

function* drawMetal(c: GenCtx): Steps {
  const { g, size, base, grime, seed, rand } = c;
  const streak = new TileNoise(seed ^ 0x3001, 2, 96); // long along u: horizontal streaks
  const hair = new TileNoise(seed ^ 0x3002, 6, 192);
  const chips = new TileNoise(seed ^ 0x3003, 28, 28);
  const bare = parseHex(BARE_STEEL);
  const ribPeriod = size / 8; // shallow vertical corrugation, eight ribs per repeat
  yield* paintPixels(c, (x, y, u, v, px) => {
    let f = 1 + (streak.fbm(u, v, 3) - 0.5) * 0.2 + (hair.sample(u, v) - 0.5) * 0.06;
    // Corrugation: light on the left flank of a rib, dark on the right.
    f += 0.05 * Math.sin((TAU * (x + 0.5)) / ribPeriod);
    const de = borderDistance(x, y, size);
    // Slightly lighter band along the plate edge.
    if (de < 10) {
      const t = 1 - de / 10;
      f += 0.07 * t * t;
    }
    // Grime pools towards the bottom of the plate.
    f -= (0.03 + 0.06 * grime) * baseDirt(v);
    px.r = base.r * f;
    px.g = base.g * f;
    px.b = base.b * f;
    // Painted-edge wear: irregular chips along the border show bare steel.
    const wear = smoothstep(0.6, 0.8, chips.sample(u, v)) * (1 - smoothstep(1, 8, de));
    if (wear > 0.01) blend(px, bare, 0.5 * wear);
  });
  yield;

  drawScratches(c, {
    count: Math.round(size * 0.12),
    minLen: 10,
    maxLen: 70,
    angle: 0,
    spread: 0.18,
    light: rgba(WHITE, 0.2),
    dark: rgba(BLACK, 0.18),
    width: 0.8,
  });

  // Grime: dark smudges stretched along the brushing direction.
  const smudges = Math.round(grime * 10);
  for (let i = 0; i < smudges; i++) {
    softBlob(g, size, rand() * size, rand() * size, 16 + rand() * 30, 4 + rand() * 8, BLACK, 0.06 + 0.1 * grime);
  }
}

function* drawGrate(c: GenCtx): Steps {
  const { g, size, color, grime, rand } = c;
  const pitch = size / 8; // 8 x 8 holes per repeat
  const bar = shade(color, 0.82);
  const light = mix(bar, WHITE, 0.35);
  const dark = shade(bar, 0.55);
  g.clearRect(0, 0, size, size); // everything starts transparent: that is the open area

  // Long bars (horizontal), then the shorter cross-bars on top of them. Indices run to 8 so the
  // half bars at the tile edge are drawn from both sides.
  const longW = 6;
  for (let j = 0; j <= 8; j++) {
    const y = j * pitch;
    g.fillStyle = bar;
    g.fillRect(0, y - longW / 2, size, longW);
    g.fillStyle = light;
    g.fillRect(0, y - longW / 2, size, 1);
    g.fillStyle = dark;
    g.fillRect(0, y + longW / 2 - 1, size, 1);
  }
  const crossW = 4;
  for (let i = 0; i <= 8; i++) {
    const x = i * pitch;
    g.fillStyle = bar;
    g.fillRect(x - crossW / 2, 0, crossW, size);
    g.fillStyle = light;
    g.fillRect(x - crossW / 2, 0, 1, size);
    g.fillStyle = dark;
    g.fillRect(x + crossW / 2 - 1, 0, 1, size);
  }

  // Wear and grime only land on the bars; the alpha channel stays untouched.
  g.globalCompositeOperation = "source-atop";
  drawScratches(c, {
    count: 40,
    minLen: 6,
    maxLen: 30,
    angle: 0,
    spread: 1.2,
    light: rgba(WHITE, 0.16),
    dark: rgba(BLACK, 0.2),
    width: 0.8,
  });
  if (grime > 0) {
    speckle(c, rgba(DIRT, 0.25 + 0.3 * grime), Math.round(grime * 160), 1, 2);
    for (let i = 0; i < Math.round(grime * 8); i++) {
      softBlob(g, size, rand() * size, rand() * size, 16 + rand() * 24, 8 + rand() * 12, BLACK, 0.18 * grime);
    }
  }
  g.globalCompositeOperation = "source-over";
  yield;
}

function* drawStripes(c: GenCtx): Steps {
  const { g, size, base, color, grime, seed, rand } = c;
  // Dark partner stripe, or off-white when the base colour is itself dark.
  const alt = parseHex(luminance(color) < 0.3 ? ARCH.offWhite : STRIPE_DARK);
  yield* paintStripes(c, size / 8, base, alt);
  const wearField = new TileNoise(seed ^ 0x4001, 5, 5);

  // Worn paint: chips showing bare surface, clustered where the field is high, plus scratches.
  g.fillStyle = rgba("#8d8c88", 0.55);
  let placed = 0;
  for (let tries = 0; placed < 40 && tries < 400; tries++) {
    const cx = rand() * size;
    const cy = rand() * size;
    const r = 1.2 + rand() * 3;
    const pts: Array<[number, number]> = [];
    const verts = 4 + Math.floor(rand() * 3);
    for (let k = 0; k < verts; k++) {
      const a = (k / verts) * TAU + rand() * 0.8;
      const rr = r * (0.55 + rand() * 0.6);
      pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * 0.8]);
    }
    if (wearField.sample(cx / size, cy / size) < 0.45) continue;
    placed++;
    drawWrapped(g, size, cx - r * 1.3, cy - r * 1.3, cx + r * 1.3, cy + r * 1.3, () => {
      g.beginPath();
      g.moveTo(pts[0][0], pts[0][1]);
      for (let k = 1; k < pts.length; k++) g.lineTo(pts[k][0], pts[k][1]);
      g.closePath();
      g.fill();
    });
  }
  drawScratches(c, {
    count: 18,
    minLen: 8,
    maxLen: 40,
    angle: Math.PI * 0.25,
    spread: 2.4,
    light: rgba("#d8d6d0", 0.3),
    dark: rgba(BLACK, 0.25),
    width: 0.8,
  });
  if (grime > 0) {
    speckle(c, rgba(BLACK, 0.3), Math.round(grime * 260), 1, 2);
    const smudges = Math.round(grime * 6);
    for (let i = 0; i < smudges; i++) {
      softBlob(g, size, rand() * size, rand() * size, 12 + rand() * 24, 8 + rand() * 14, BLACK, 0.12 * grime);
    }
  }
  yield;
}

function* drawGlass(c: GenCtx): Steps {
  const { g, size, color, grime, seed } = c;
  const tint = parseHex(mix(color, WHITE, 0.72));
  const smudge = new TileNoise(seed ^ 0x5001, 4, 4);
  // Diagonal highlight bands in fract(u + v): [centre, half width, strength].
  const bands: ReadonlyArray<readonly [number, number, number]> = [
    [0.2, 0.06, 0.3],
    [0.31, 0.014, 0.35],
    [0.64, 0.045, 0.18],
  ];
  const white: Rgb = { r: 255, g: 255, b: 255 };
  yield* paintPixels(c, (_x, _y, u, v, px) => {
    // cos keeps the vertical gradient periodic.
    const f = 1 + 0.05 * Math.cos(TAU * v);
    px.r = tint.r * f;
    px.g = tint.g * f;
    px.b = tint.b * f;
    const w = u + v - Math.floor(u + v);
    for (const [centre, hw, strength] of bands) {
      let d = Math.abs(w - centre);
      if (d > 0.5) d = 1 - d;
      blend(px, white, strength * (1 - smoothstep(hw * 0.4, hw, d)));
    }
    if (grime > 0) {
      const dirty = grime * 0.12 * smoothstep(0.55, 0.8, smudge.fbm(u, v, 2));
      px.r *= 1 - dirty;
      px.g *= 1 - dirty;
      px.b *= 1 - dirty;
    }
  });

  // Pane frame: dark line on the border (one side per tile edge) and a thin inner highlight.
  g.fillStyle = rgba(mix(color, "#101417", 0.7), 0.85);
  g.fillRect(0, 0, size, 1);
  g.fillRect(0, size - 1, size, 1);
  g.fillRect(0, 1, 1, size - 2);
  g.fillRect(size - 1, 1, 1, size - 2);
  g.fillStyle = rgba(WHITE, 0.45);
  g.fillRect(1, 1, size - 2, 1);
  g.fillRect(1, 2, 1, size - 4);
}

function* drawWood(c: GenCtx): Steps {
  const { g, size, base, grime, seed, rand } = c;
  const planks = 4;
  const plankH = size / planks;
  const s = size / 512; // the layout below is authored for 512 px
  const warp = new TileNoise(seed ^ 0x6001, 2, 4);
  const grainA = new TileNoise(seed ^ 0x6002, 3, 72); // stretched along u
  const grainB = new TileNoise(seed ^ 0x6003, 2, 220);

  // Per-plank tone, slight warm/cool shift and noise offset so the planks do not repeat each other.
  const plankBase: Rgb[] = [];
  const plankOffset: number[] = [];
  for (let p = 0; p < planks; p++) {
    const tone = 0.86 + rand() * 0.28;
    const shift = (rand() - 0.5) * 0.08;
    plankBase.push({ r: base.r * tone * (1 + shift), g: base.g * tone, b: base.b * tone * (1 - shift) });
    plankOffset.push(rand());
  }

  yield* paintPixels(c, (_x, y, u, v, px) => {
    const p = Math.min(planks - 1, Math.floor(y / plankH));
    const o = plankOffset[p];
    const col = plankBase[p];
    // Grain lines wander: the lookup row is displaced by a slow periodic warp.
    const gv = v + (warp.sample(u + o, v) - 0.5) * 0.05 + p * 0.37;
    let f = 1 + (grainA.fbm(u + o, gv, 3) - 0.5) * 0.45;
    f -= smoothstep(0.62, 0.9, grainB.sample(u + o, gv)) * 0.12;
    // Rounded plank edges.
    const ly = y - p * plankH;
    const de = Math.min(ly, plankH - 1 - ly) + 0.5;
    f -= 0.1 * Math.exp(-de / (3 * s));
    px.r = col.r * f;
    px.g = col.g * f;
    px.b = col.b * f;
  });
  yield;

  // A few knots: concentric dark rings around a darker core.
  for (let k = 0; k < 3; k++) {
    const p = Math.floor(rand() * planks);
    const kx = rand() * size;
    const ky = p * plankH + 26 * s + rand() * (plankH - 52 * s);
    const rx = (12 + rand() * 10) * s;
    const ry = rx * 0.4;
    drawWrapped(g, size, kx - rx - 2, ky - ry - 2, kx + rx + 2, ky + ry + 2, () => {
      g.strokeStyle = rgba("#28160a", 0.22);
      g.lineWidth = 1.3 * s;
      for (let ring = 0; ring < 5; ring++) {
        const q = 1 - ring * 0.18;
        g.beginPath();
        g.ellipse(kx, ky, rx * q, ry * q, 0, 0, TAU);
        g.stroke();
      }
      g.fillStyle = rgba("#23120a", 0.55);
      g.beginPath();
      g.ellipse(kx, ky, rx * 0.28, ry * 0.28, 0, 0, TAU);
      g.fill();
    });
  }

  // Dark seams between planks (the seam at 0 and size is the same one) with a light lip below.
  for (let p = 0; p <= planks; p++) {
    g.fillStyle = rgba("#140c06", 0.7);
    g.fillRect(0, p * plankH - 1, size, 2);
    g.fillStyle = rgba("#ffe6c8", 0.14);
    g.fillRect(0, p * plankH + 1, size, 1);
  }

  // Staggered end joints, one per plank.
  const joints = [0.62, 0.17, 0.83, 0.38];
  for (let p = 0; p < planks; p++) {
    const jx = Math.round((joints[p] + (rand() - 0.5) * 0.04) * size);
    g.fillStyle = rgba("#140c06", 0.65);
    g.fillRect(jx - 1, p * plankH + 1, 2, plankH - 1);
    g.fillStyle = rgba("#ffe6c8", 0.14);
    g.fillRect(jx + 1, p * plankH + 1, 1, plankH - 1);
  }

  if (grime > 0) {
    // Dirt settles along the seams and in scuffed patches.
    for (let p = 0; p <= planks; p++) {
      const y = p * plankH;
      const grad = g.createLinearGradient(0, y - 12 * s, 0, y + 12 * s);
      grad.addColorStop(0, rgba(DIRT, 0));
      grad.addColorStop(0.5, rgba(DIRT, 0.2 * grime));
      grad.addColorStop(1, rgba(DIRT, 0));
      g.fillStyle = grad;
      g.fillRect(0, y - 12 * s, size, 24 * s);
    }
    const blobs = Math.round(grime * 10);
    for (let i = 0; i < blobs; i++) {
      softBlob(g, size, rand() * size, rand() * size, (30 + rand() * 60) * s, (10 + rand() * 18) * s, DIRT, 0.05 + 0.1 * grime);
    }
  }
}

function* drawAsphalt(c: GenCtx): Steps {
  const { g, size, color, base, grime, seed, rand } = c;
  const s = size / 512; // the layout below is authored for 512 px
  const patches = new TileNoise(seed ^ 0x7001, 3, 3);
  const mid = new TileNoise(seed ^ 0x7002, 14, 14);
  yield* paintPixels(c, (_x, _y, u, v, px) => {
    // Low-frequency patches, mid-scale mottling and per-pixel grain.
    const f = 1 + (patches.fbm(u, v, 3) - 0.5) * 0.22 + (mid.sample(u, v) - 0.5) * 0.08 + (rand() - 0.5) * 0.12;
    px.r = base.r * f;
    px.g = base.g * f;
    px.b = base.b * f;
  });
  yield;

  // Aggregate: many small light and dark stones.
  speckle(c, rgba(BLACK, 0.28), Math.round(1300 * s * s), 1, 2);
  speckle(c, rgba(mix(color, WHITE, 0.4), 0.3), Math.round(900 * s * s), 1, 2);
  speckle(c, rgba(mix(color, WHITE, 0.65), 0.22), Math.round(260 * s * s), 1, 2);

  // Tyre-polish smears along two wheel paths; grime makes them darker.
  for (const lane of [0.28, 0.72]) {
    for (let i = 0; i < 4; i++) {
      const x = rand() * size;
      const rx = (90 + rand() * 120) * s;
      softBlob(g, size, x, lane * size + (rand() - 0.5) * 18 * s, rx, (20 + rand() * 12) * s, BLACK, 0.05 + 0.12 * grime);
      softBlob(g, size, x + 30 * s, lane * size, rx * 0.7, 8 * s, WHITE, 0.03);
    }
  }

  // Two or three hairline cracks (random walks) that wrap around the tile.
  const cracks = 2 + Math.floor(rand() * 2);
  for (let k = 0; k < cracks; k++) {
    let x = rand() * size;
    let y = rand() * size;
    let a = rand() * TAU;
    const pts: number[] = [x, y];
    const segs = 22 + Math.floor(rand() * 18);
    for (let i = 0; i < segs; i++) {
      a += (rand() - 0.5) * 0.9;
      const len = (5 + rand() * 9) * s;
      x += Math.cos(a) * len;
      y += Math.sin(a) * len;
      pts.push(x, y);
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < pts.length; i += 2) {
      minX = Math.min(minX, pts[i]);
      maxX = Math.max(maxX, pts[i]);
      minY = Math.min(minY, pts[i + 1]);
      maxY = Math.max(maxY, pts[i + 1]);
    }
    drawWrapped(g, size, minX - 2, minY - 2, maxX + 2, maxY + 2, () => {
      g.lineJoin = "round";
      g.lineCap = "round";
      for (const [dx, style, width] of [
        [1, rgba(WHITE, 0.07), 1.2],
        [0, rgba("#050506", 0.6), 1.4],
      ] as const) {
        g.strokeStyle = style;
        g.lineWidth = width * Math.max(1, s);
        g.beginPath();
        g.moveTo(pts[0] + dx, pts[1] + dx);
        for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i] + dx, pts[i + 1] + dx);
        g.stroke();
      }
    });
  }
}

const SURFACES: Record<TextureKind, (c: GenCtx) => Steps> = {
  plain: drawPlain,
  concrete: drawConcrete,
  tiles: drawTiles,
  panel: drawPanel,
  metal: drawMetal,
  grate: drawGrate,
  stripes: drawStripes,
  glass: drawGlass,
  wood: drawWood,
  asphalt: drawAsphalt,
};

// ---------------------------------------------------------------------------
// Camo painters (256 px, no grime)
// ---------------------------------------------------------------------------

const CARBON_BASE = parseHex("#15171a");
const CARBON_HIGH = parseHex("#2e3238");

/** 2/2 twill carbon-fibre weave, 16 x 16 cells per repeat. */
function* drawCarbon(c: GenCtx): Steps {
  const cell = c.size / 16;
  yield* paintPixels(c, (x, y, u, v, px) => {
    const cx = Math.floor(x / cell);
    const cy = Math.floor(y / cell);
    const lx = ((x % cell) + 0.5) / cell;
    const ly = ((y % cell) + 0.5) / cell;
    // 2/2 twill: horizontal tows lie on top for two cells, vertical tows for the next two.
    const horizontal = ((cx + cy) & 3) < 2;
    const across = horizontal ? ly : lx;
    const along = horizontal ? lx : ly;
    // Round tow profile, brightest mid-tow; tow ends dip under the crossing tow.
    const profile = Math.sin(Math.PI * across) ** 2;
    const occ = smoothstep(0, 0.25, along) * smoothstep(0, 0.25, 1 - along);
    const strand = 0.5 + 0.5 * Math.sin(TAU * across * 6);
    const sheen = 1 + 0.18 * Math.sin(TAU * (u + v));
    const t = clamp01(profile * occ * (0.55 + 0.45 * strand) * sheen * (horizontal ? 1 : 0.8));
    const shadow = 0.7 + 0.3 * occ;
    px.r = (CARBON_BASE.r + (CARBON_HIGH.r - CARBON_BASE.r) * t) * shadow;
    px.g = (CARBON_BASE.g + (CARBON_HIGH.g - CARBON_BASE.g) * t) * shadow;
    px.b = (CARBON_BASE.b + (CARBON_HIGH.b - CARBON_BASE.b) * t) * shadow;
  });
}

/** Yellow / black hazard stripes (period 32 px = 8 per repeat) with worn, wobbly edges. */
function* drawHazard(c: GenCtx): Steps {
  const { g, size, seed, rand } = c;
  const wobble = new TileNoise(seed ^ 0x8001, 16, 16);
  yield* paintStripes(c, 32, parseHex("#f2c230"), parseHex(STRIPE_DARK), { noise: wobble, amp: 3 });
  g.fillStyle = rgba("#6d6a5c", 0.5);
  for (let i = 0; i < 60; i++) {
    const w = 1 + Math.floor(rand() * 3);
    const h = 1 + Math.floor(rand() * 3);
    fillRectWrapped(g, size, Math.floor(rand() * size), Math.floor(rand() * size), w, h);
  }
  drawScratches(c, {
    count: 22,
    minLen: 6,
    maxLen: 34,
    angle: Math.PI * 0.25,
    spread: 2.4,
    light: rgba("#e8e4d4", 0.3),
    dark: rgba(BLACK, 0.25),
    width: 0.8,
  });
}

/**
 * Blocky digital snow camo. Blotches come from thresholded fbm sampled on 16 px cells; near a
 * threshold the sampling drops to 8 px cells for a finer pixelated edge. Thresholds are quantiles of
 * the sampled fields so the coverage is fixed whatever the noise distribution is.
 */
function* drawArctic(c: GenCtx): Steps {
  const { size, seed, rand } = c;
  const baseCol = parseHex("#e9eef2");
  const midCol = parseHex("#a9b8c4");
  const darkCol = parseHex("#6f8293");
  const noiseMid = new TileNoise(seed ^ 0x9001, 3, 3);
  const noiseDark = new TileNoise(seed ^ 0x9002, 4, 4);

  // Field sampled at the centre of every cell of `cellPx` pixels.
  const field = (noise: TileNoise, cellPx: number): Float32Array => {
    const n = size / cellPx;
    const out = new Float32Array(n * n);
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) out[j * n + i] = noise.fbm((i + 0.5) / n, (j + 0.5) / n, 3);
    }
    return out;
  };
  const midBig = field(noiseMid, 16);
  const midSmall = field(noiseMid, 8);
  const darkBig = field(noiseDark, 16);
  const darkSmall = field(noiseDark, 8);
  const midT = quantile(midBig, 0.58);
  const darkT = quantile(darkBig, 0.82);
  const band = 0.04;
  const nBig = size / 16;
  const nSmall = size / 8;

  yield* paintPixels(c, (x, y, _u, _v, px) => {
    const bi = (y >> 4) * nBig + (x >> 4);
    const si = (y >> 3) * nSmall + (x >> 3);
    const m = Math.abs(midBig[bi] - midT) < band ? midSmall[si] : midBig[bi];
    const d = Math.abs(darkBig[bi] - darkT) < band ? darkSmall[si] : darkBig[bi];
    const col = d > darkT ? darkCol : m > midT ? midCol : baseCol;
    const grain = 1 + (rand() - 0.5) * 0.03;
    px.r = col.r * grain;
    px.g = col.g * grain;
    px.b = col.b * grain;
  });
}

/** Charred crust with glowing lava veins along the zero crossings of fbm (ridged noise). */
function* drawEmber(c: GenCtx): Steps {
  const { size, seed } = c;
  const ridge = new TileNoise(seed ^ 0xa001, 3, 3);
  const grain = new TileNoise(seed ^ 0xa002, 24, 24);
  const haloCol = parseHex("#4a1606");
  const veinCol = parseHex("#ff5a10");
  const coreCol = parseHex("#ffb030");
  const crust = parseHex("#17120f");

  // |n - 0.5| is small along veins; the thresholds are quantiles so veins cover a fixed share of
  // the tile: 3.5 % hot core, 13 % vein, 32 % glowing halo.
  const dist = new Float32Array(size * size);
  const inv = 1 / size;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      dist[y * size + x] = Math.abs(ridge.fbm((x + 0.5) * inv, (y + 0.5) * inv, 4) - 0.5);
    }
    if (y % ROWS_PER_STEP === ROWS_PER_STEP - 1) yield;
  }
  const coreT = quantile(dist, 0.035);
  const veinT = quantile(dist, 0.13);
  const haloT = quantile(dist, 0.32);
  yield;

  yield* paintPixels(c, (x, y, u, v, px) => {
    const d = dist[y * size + x];
    const gr = grain.sample(u, v);
    const k = 0.8 + gr * 0.5;
    px.r = crust.r * k;
    px.g = crust.g * k;
    px.b = crust.b * k;
    const halo = 1 - smoothstep(veinT, haloT, d);
    const heat = 1 - smoothstep(coreT, veinT, d);
    blend(px, haloCol, halo * halo * 0.55);
    blend(px, veinCol, smoothstep(0, 0.7, heat));
    blend(px, coreCol, smoothstep(0.65, 1, heat) * (0.7 + 0.3 * gr));
  });
}

/** Iridescent rainbow (hue = fract(2u + v)) under irregular faceted shards. */
function* drawPrism(c: GenCtx): Steps {
  const { rand } = c;
  const n = 8; // shard cells per repeat (periodic Voronoi)
  const count = n * n;
  const jx = new Float32Array(count);
  const jy = new Float32Array(count);
  const facetLight = new Float32Array(count);
  const facetHue = new Float32Array(count);
  const gradX = new Float32Array(count);
  const gradY = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    jx[i] = 0.15 + rand() * 0.7;
    jy[i] = 0.15 + rand() * 0.7;
    facetLight[i] = rand() * 2 - 1;
    facetHue[i] = rand() * 2 - 1;
    gradX[i] = rand() * 2 - 1;
    gradY[i] = rand() * 2 - 1;
  }
  yield* paintPixels(c, (_x, _y, u, v, px) => {
    const fx = u * n;
    const fy = v * n;
    const ix = Math.floor(fx);
    const iy = Math.floor(fy);
    let d1 = Infinity;
    let d2 = Infinity;
    let best = 0;
    let bx = 0;
    let by = 0;
    for (let dj = -1; dj <= 1; dj++) {
      for (let di = -1; di <= 1; di++) {
        const cx = ix + di;
        const cy = iy + dj;
        const idx = (((cy % n) + n) % n) * n + (((cx % n) + n) % n);
        const sx = cx + jx[idx];
        const sy = cy + jy[idx];
        const d = (fx - sx) * (fx - sx) + (fy - sy) * (fy - sy);
        if (d < d1) {
          d2 = d1;
          d1 = d;
          best = idx;
          bx = sx;
          by = sy;
        } else if (d < d2) {
          d2 = d;
        }
      }
    }
    // Facet lightness: per-shard offset, a tilt gradient across the shard, the global diagonal sheen.
    let l =
      0.55 +
      facetLight[best] * 0.14 +
      ((fx - bx) * gradX[best] + (fy - by) * gradY[best]) * 0.14 +
      0.05 * Math.sin(TAU * (u + v));
    // Bright crease where two shards meet.
    const edge = Math.sqrt(d2) - Math.sqrt(d1);
    l += 0.14 * (1 - smoothstep(0, 0.06, edge));
    const h = 2 * u + v + facetHue[best] * 0.03;
    hslToPx(h - Math.floor(h), 0.8, clamp01(l), px);
  });
}

const CAMO_DRAWERS: Partial<Record<CamoName, (c: GenCtx) => Steps>> = {
  carbon: drawCarbon,
  hazard: drawHazard,
  arctic: drawArctic,
  ember: drawEmber,
  prism: drawPrism,
};

// ---------------------------------------------------------------------------
// Decal atlas (transparent background, one DECAL_KINDS cell each)
// ---------------------------------------------------------------------------

/** Cell edge in the coordinates the cell painters are authored in. */
const CELL = 128;
const PAINT_WHITE = "#ecebe6";
const PAINT_YELLOW = "#f2c230";

/** Soft round blob in cell coordinates. */
function blob(g: Ctx2D, x: number, y: number, rx: number, ry: number, color: string, alpha: number): void {
  softBlob(g, CELL, x, y, rx, ry, color, alpha);
}

/** Erases scattered flecks so painted marks look worn. */
function wearOut(g: Ctx2D, rand: () => number, count: number, maxSize: number): void {
  g.globalCompositeOperation = "destination-out";
  for (let i = 0; i < count; i++) {
    g.fillStyle = rgba(BLACK, 0.25 + rand() * 0.5);
    const w = 1 + rand() * maxSize;
    g.fillRect(rand() * CELL, rand() * CELL, w, 1 + rand() * maxSize * 0.6);
  }
  g.globalCompositeOperation = "source-over";
}

/** Polyline strokes of the stencil glyphs, in a 128 px cell (the digit/letter box is x 36..92, y 22..106). */
const GLYPHS: Record<string, ReadonlyArray<ReadonlyArray<readonly [number, number]>>> = {
  n1: [
    [[64, 22], [64, 106]],
    [[44, 42], [64, 22]],
  ],
  n2: [[[40, 42], [50, 26], [80, 26], [90, 40], [90, 54], [40, 106], [92, 106]]],
  n3: [
    [[40, 26], [90, 26], [90, 106], [40, 106]],
    [[60, 64], [90, 64]],
  ],
  n4: [
    [[78, 22], [78, 106]],
    [[78, 22], [38, 78], [94, 78]],
  ],
  A: [
    [[38, 106], [64, 22], [90, 106]],
    [[50, 78], [78, 78]],
  ],
  B: [
    [[42, 22], [42, 106]],
    [[42, 22], [76, 22], [88, 32], [88, 50], [76, 62], [42, 62]],
    [[76, 62], [90, 74], [90, 94], [76, 106], [42, 106]],
  ],
  C: [[[90, 36], [78, 22], [52, 22], [40, 36], [40, 92], [52, 106], [78, 106], [90, 92]]],
  D: [
    [[42, 22], [42, 106]],
    [[42, 22], [70, 22], [90, 42], [90, 86], [70, 106], [42, 106]],
  ],
};

type CellPainter = (g: Ctx2D, rand: () => number) => void;

function paintGlyph(kind: string): CellPainter {
  return (g, rand) => {
    g.strokeStyle = PAINT_WHITE;
    g.lineWidth = 15;
    g.lineJoin = "miter";
    g.lineCap = "butt";
    for (const stroke of GLYPHS[kind]) {
      g.beginPath();
      g.moveTo(stroke[0][0], stroke[0][1]);
      for (let i = 1; i < stroke.length; i++) g.lineTo(stroke[i][0], stroke[i][1]);
      g.stroke();
    }
    // Stencil bridge through the middle of the glyph, then wear.
    if (kind !== "n1") {
      g.globalCompositeOperation = "destination-out";
      g.fillStyle = BLACK;
      g.fillRect(30, 62, 70, 4);
      g.globalCompositeOperation = "source-over";
    }
    wearOut(g, rand, 50, 7);
  };
}

const CELL_PAINTERS: Record<DecalKind, CellPainter> = {
  oil: (g, rand) => {
    for (let i = 0; i < 7; i++) {
      const r = 16 + rand() * 20;
      blob(g, 64 + (rand() - 0.5) * 46, 64 + (rand() - 0.5) * 38, r, r * (0.6 + rand() * 0.4), "#0d0b09", 0.7);
    }
    blob(g, 62, 60, 20, 14, "#000000", 0.5);
    // A faint rainbow-less sheen along the upper edge.
    g.fillStyle = rgba("#6b7078", 0.12);
    g.beginPath();
    g.ellipse(58, 50, 18, 5, -0.3, 0, TAU);
    g.fill();
  },
  streak: (g, rand) => {
    for (let i = 0; i < 7; i++) {
      const x = 14 + rand() * 100;
      const w = 4 + rand() * 12;
      const len = 50 + rand() * 72;
      const grad = g.createLinearGradient(0, 4, 0, 4 + len);
      // Fades in under the source, darkest near the top, thins out to nothing at the bottom.
      grad.addColorStop(0, rgba("#1b1712", 0));
      grad.addColorStop(0.08, rgba("#1b1712", 0.4));
      grad.addColorStop(0.55, rgba("#1b1712", 0.2));
      grad.addColorStop(1, rgba("#1b1712", 0));
      g.fillStyle = grad;
      g.fillRect(x - w / 2, 4, w, len);
      blob(g, x, 8, w * 1.5, 6, "#1b1712", 0.25);
    }
  },
  scuff: (g, rand) => {
    g.lineCap = "round";
    for (let i = 0; i < 9; i++) {
      const x = 14 + rand() * 100;
      const y = 14 + rand() * 100;
      const len = 20 + rand() * 40;
      const a = -0.6 + (rand() - 0.5) * 0.35;
      const dx = Math.cos(a) * len;
      const dy = Math.sin(a) * len;
      const grad = g.createLinearGradient(x, y, x + dx, y + dy);
      const col = rand() < 0.75 ? "#1d1b19" : "#cfcdc6";
      grad.addColorStop(0, rgba(col, 0));
      grad.addColorStop(0.3, rgba(col, 0.4));
      grad.addColorStop(1, rgba(col, 0));
      g.strokeStyle = grad;
      g.lineWidth = 2 + rand() * 5;
      g.beginPath();
      g.moveTo(x, y);
      g.lineTo(x + dx, y + dy);
      g.stroke();
    }
  },
  splat: (g, rand) => {
    for (let i = 0; i < 60; i++) {
      const a = rand() * TAU;
      const d = rand() * rand() * 52;
      const r = 0.8 + (1 - d / 52) * rand() * 6;
      g.fillStyle = rgba("#14110d", 0.35 + rand() * 0.4);
      fillCircle(g, 64 + Math.cos(a) * d, 64 + Math.sin(a) * d, r);
    }
    blob(g, 64, 64, 14, 12, "#14110d", 0.55);
  },
  chevron: (g, rand) => {
    g.beginPath();
    g.moveTo(10, 78);
    g.lineTo(64, 24);
    g.lineTo(118, 78);
    g.lineTo(118, 106);
    g.lineTo(64, 52);
    g.lineTo(10, 106);
    g.closePath();
    g.fillStyle = PAINT_YELLOW;
    g.fill();
    g.lineJoin = "miter";
    g.lineWidth = 5;
    g.strokeStyle = rgba(STRIPE_DARK, 0.9);
    g.stroke();
    wearOut(g, rand, 50, 7);
  },
  arrow: (g, rand) => {
    g.beginPath();
    g.moveTo(64, 10);
    g.lineTo(108, 62);
    g.lineTo(82, 62);
    g.lineTo(82, 118);
    g.lineTo(46, 118);
    g.lineTo(46, 62);
    g.lineTo(20, 62);
    g.closePath();
    g.fillStyle = PAINT_WHITE;
    g.fill();
    wearOut(g, rand, 60, 8);
  },
  crack: (g, rand) => {
    g.lineCap = "round";
    g.lineJoin = "round";
    const branch = (x: number, y: number, a: number, steps: number, w: number): void => {
      g.strokeStyle = rgba("#0b0a09", 0.75);
      g.lineWidth = w;
      g.beginPath();
      g.moveTo(x, y);
      for (let i = 0; i < steps; i++) {
        a += (rand() - 0.5) * 0.9;
        x += Math.cos(a) * (5 + rand() * 6);
        y += Math.sin(a) * (5 + rand() * 6);
        g.lineTo(x, y);
        if (i > 2 && rand() < 0.18) branch(x, y, a + (rand() < 0.5 ? 0.9 : -0.9), Math.floor(steps / 2), w * 0.7);
      }
      g.stroke();
    };
    branch(18, 64, 0.1, 18, 1.8);
  },
  skid: (g) => {
    for (const x of [40, 88]) {
      const grad = g.createLinearGradient(0, 0, 0, CELL);
      grad.addColorStop(0, rgba("#0f0e0d", 0));
      grad.addColorStop(0.25, rgba("#0f0e0d", 0.5));
      grad.addColorStop(0.75, rgba("#0f0e0d", 0.5));
      grad.addColorStop(1, rgba("#0f0e0d", 0));
      g.fillStyle = grad;
      g.beginPath();
      g.moveTo(x - 6, 0);
      g.quadraticCurveTo(x + 4, 64, x - 6, 128);
      g.lineTo(x + 6, 128);
      g.quadraticCurveTo(x + 14, 64, x + 6, 0);
      g.closePath();
      g.fill();
    }
  },
  n1: paintGlyph("n1"),
  n2: paintGlyph("n2"),
  n3: paintGlyph("n3"),
  n4: paintGlyph("n4"),
  A: paintGlyph("A"),
  B: paintGlyph("B"),
  C: paintGlyph("C"),
  D: paintGlyph("D"),
};

function* drawDecalAtlas(c: GenCtx): Steps {
  const { g, size, rand } = c;
  const cell = size / DECAL_GRID;
  g.clearRect(0, 0, size, size);
  for (let i = 0; i < DECAL_KINDS.length; i++) {
    g.save();
    g.translate((i % DECAL_GRID) * cell, Math.floor(i / DECAL_GRID) * cell);
    g.scale(cell / CELL, cell / CELL);
    g.beginPath();
    g.rect(0, 0, CELL, CELL);
    g.clip();
    CELL_PAINTERS[DECAL_KINDS[i]](g, rand);
    g.restore();
    yield;
  }
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/** Painter for one job; the caller owns the canvas (`job.size` square) and iterates the steps. */
export function paintJob(job: TextureJob, g: Ctx2D): Steps {
  const seed = hashString(job.key);
  const color = job.type === "surface" ? job.paint : WHITE;
  const c: GenCtx = {
    g,
    size: job.size,
    color,
    base: parseHex(color),
    grime: job.type === "surface" ? job.grime : 0,
    seed,
    rand: mulberry32(seed),
  };
  switch (job.type) {
    case "surface":
      return SURFACES[job.kind](c);
    case "camo": {
      const draw = CAMO_DRAWERS[job.camo];
      if (!draw) throw new Error(`camo "${job.camo}" has no texture`);
      return draw(c);
    }
    case "decals":
      return drawDecalAtlas(c);
  }
}

/** True when the camo has a generated texture ("none" and "gold" are plain colours). */
export function camoHasTexture(camo: CamoName): boolean {
  return CAMO_DRAWERS[camo] !== undefined;
}
